import { useState, useEffect, useRef, useCallback } from 'react';

interface DebouncedLoadingOptions {
  /** Delay before showing the loading spinner (ms) */
  showDelay?: number;
  /** Minimum time to display the spinner once shown (ms) */
  minDisplayTime?: number;
  /** Cooldown period to prevent rapid reappearing (ms) */
  cooldownPeriod?: number;
}

interface DebouncedLoadingState {
  /** Whether the loading spinner should be displayed */
  isVisible: boolean;
  /** Whether loading is currently active (even if not visible) */
  isLoading: boolean;
}

/**
 * Modern debounced loading hook that prevents spinner flickering
 * and provides smooth loading state transitions
 */
export function useDebouncedLoading(
  isLoading: boolean,
  options: DebouncedLoadingOptions = {}
): DebouncedLoadingState {
  const {
    showDelay = 150,
    minDisplayTime = 300,
    cooldownPeriod = 100
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const [lastHideTime, setLastHideTime] = useState(0);
  
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const visibilityStartRef = useRef<number | null>(null);

  const clearTimeouts = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isLoading) {
      // Starting to load
      clearTimeouts();
      
      // Check cooldown period
      const now = Date.now();
      const timeSinceLastHide = now - lastHideTime;
      
      if (timeSinceLastHide < cooldownPeriod) {
        // Still in cooldown, show immediately
        setIsVisible(true);
        visibilityStartRef.current = now;
      } else {
        // Not in cooldown, use show delay
        showTimeoutRef.current = setTimeout(() => {
          setIsVisible(true);
          visibilityStartRef.current = Date.now();
        }, showDelay);
      }
    } else {
      // Finished loading
      clearTimeouts();
      
      if (isVisible && visibilityStartRef.current) {
        const visibilityDuration = Date.now() - visibilityStartRef.current;
        
        if (visibilityDuration < minDisplayTime) {
          // Haven't shown long enough, delay hiding
          const remainingTime = minDisplayTime - visibilityDuration;
          hideTimeoutRef.current = setTimeout(() => {
            setIsVisible(false);
            setLastHideTime(Date.now());
            visibilityStartRef.current = null;
          }, remainingTime);
        } else {
          // Shown long enough, hide immediately
          setIsVisible(false);
          setLastHideTime(Date.now());
          visibilityStartRef.current = null;
        }
      } else {
        // Wasn't visible, just clear any pending show timeout
        setIsVisible(false);
        visibilityStartRef.current = null;
      }
    }

    // Cleanup on unmount
    return clearTimeouts;
  }, [isLoading, isVisible, lastHideTime, showDelay, minDisplayTime, cooldownPeriod, clearTimeouts]);

  return {
    isVisible,
    isLoading
  };
}